import React, { useContext } from "react"
import { useWatch } from "react-hook-form"

import { SelectOption } from "shared/types/Select"
import { InputContext } from "shared/ui/input/Input"

import { SelectValue } from "./SelectValue"

interface ReadonlyProps<T>
  extends Omit<
    React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
    "value"
  > {
  options?: SelectOption<T>[]
  value?: T | T[] | SelectOption<T> | SelectOption<T>[] | null | undefined
  render?: (
    value: T | T[] | SelectOption<T> | SelectOption<T>[] | null | undefined
  ) => React.ReactNode
}

function ReadonlySelectBase<T>({
  className,
  children,
  render,
  options,
  value,
  ...props
}: ReadonlyProps<T>) {
  const { readonly, loading } = useContext(InputContext)

  if (!readonly || loading) {
    return null
  }

  if (children) {
    return children as React.JSX.Element
  }

  if (render) {
    return render(value)
  }

  return (
    <SelectValue<T>
      {...props}
      value={value}
      options={options}
      className="input__value--readonly"
    />
  )
}

function HookFormReadonlySelect<T>(props: ReadonlyProps<T>) {
  const { name } = useContext(InputContext)
  const value = useWatch({ name }) as
    | T
    | T[]
    | SelectOption<T>
    | SelectOption<T>[]
    | null
    | undefined

  return <ReadonlySelectBase {...props} value={value} />
}

function ReadonlySelectComponent<T>(props: ReadonlyProps<T>) {
  const { name } = useContext(InputContext)

  if (name) {
    return <HookFormReadonlySelect<T> {...props} />
  }
  return <ReadonlySelectBase<T> {...props} />
}

export const ReadonlySelect = React.memo(
  ReadonlySelectComponent
) as typeof ReadonlySelectComponent
